import {
  parseISO,
  addDays,
  addMonths,
  isBefore,
  isEqual,
  getMonth,
  getYear,
  endOfWeek,
  isAfter,
  differenceInCalendarDays,
  endOfMonth,
} from 'date-fns';
import * as TabilityTypes from 'types';

export function getLastDaysOfWeekBetween(startDateStr: string, endDateStr: string) {
  const startDate = parseISO(startDateStr);
  const endDate = parseISO(endDateStr);
  const lastDays = [];

  // Get the first Monday on or after startDate
  let current = endOfWeek(startDate, { weekStartsOn: 1 }); // 1 = Monday

  if (isBefore(current, startDate)) {
    current = addDays(current, 7);
  }

  while (isBefore(current, endDate) || isEqual(current, endDate)) {
    lastDays.push(current);
    current = addDays(current, 7);
  }

  return lastDays;
}

export function getLastDaysOfMonthBetween(startDateStr: string, endDateStr: string) {
  const startDate = parseISO(startDateStr);
  const endDate = parseISO(endDateStr);
  const lastDays = [];

  let current = endOfMonth(startDate);

  while (isBefore(current, endDate) || isEqual(current, endDate)) {
    lastDays.push(current);
    current = addMonths(current, 1);
  }

  return lastDays;
}

export function getLatestCheckinByWeek(checkins: TabilityTypes.Checkin[], referenceWeekStarts: Date[]) {
  const sortedCheckins = checkins
    .map((c) => ({ ...c, checkin_date: parseISO(c.checkin_date) }))
    .sort((a: any, b: any) => b.checkin_date - a.checkin_date);

  return referenceWeekStarts.map((weekEnd) => {
    const latest = sortedCheckins.find((checkin) => {
      const d = checkin.checkin_date;
      const checkinWeekEnd = endOfWeek(d, { weekStartsOn: 1 });
      return isEqual(weekEnd, checkinWeekEnd);
    });

    return {
      date: weekEnd,
      checkin: latest || null,
    };
  });
}

export function getLatestCheckinByMonth(checkins: TabilityTypes.Checkin[], referenceMonths: Date[]) {
  const sortedCheckins = checkins
    .map((c) => ({ ...c, checkin_date: parseISO(c.checkin_date) }))
    .sort((a: any, b: any) => b.checkin_date - a.checkin_date);

  return referenceMonths.map((monthEnd) => {
    const month = getMonth(monthEnd);
    const year = getYear(monthEnd);
    const latest = sortedCheckins.find((checkin) => {
      const d = checkin.checkin_date;
      return (isBefore(d, monthEnd) || isEqual(d, monthEnd)) && getMonth(d) === month && getYear(d) === year;
    });

    return {
      date: monthEnd,
      checkin: latest || null,
    };
  });
}

export function getExpectedValuesAtDates(
  outcome: TabilityTypes.Outcome,
  plan: TabilityTypes.Plan,
  referenceDates: Date[],
) {
  if (!plan.start_at || !plan.finish_at) {
    return [];
  }

  if (outcome.outcome_type === 'improve_metric') {
    return getExpectedImproveMetricValuesAtDates(outcome, plan, referenceDates);
  }

  if (outcome.outcome_type === 'stay_above' || outcome.outcome_type === 'stay_below') {
    return getExpectedFlatMetricValuesAtDates(outcome, plan, referenceDates);
  }

  if (outcome.outcome_type === 'kpi') {
    return getExpectedKpiMetricValuesAtDates(plan, referenceDates);
  }

  if (outcome.outcome_type === 'no_metric') {
    return getExpectedNoMetricValuesAtDates(plan, referenceDates);
  }

  return [];
}

export function getExpectedImproveMetricValuesAtDates(
  outcome: TabilityTypes.Outcome,
  plan: TabilityTypes.Plan,
  referenceDates: Date[],
) {
  if (!plan.start_at || !plan.finish_at) {
    return [];
  }

  if (outcome.from === null || outcome.to === null) {
    return [];
  }

  const decimalNumbers = Math.abs(outcome.from - outcome.to) < 1 ? 2 : 0;

  const targetMilestones = JSON.parse(outcome.target_milestones) || [];
  const segments = [
    { date: parseISO(plan.start_at), value: outcome.from },
    ...targetMilestones.map((m: TabilityTypes.TargetMilestone) => ({
      date: parseISO(m.date),
      value: m.value,
    })),
    { date: parseISO(plan.finish_at), value: outcome.to },
  ].sort((a, b) => a.date - b.date);

  return referenceDates.map((date) => {
    // Skip before start or after end
    if (isBefore(date, segments[0].date) || isAfter(date, segments[segments.length - 1].date)) {
      return { date, expected: null };
    }

    // Find the segment the date falls into
    for (let i = 0; i < segments.length - 1; i++) {
      const a = segments[i];
      const b = segments[i + 1];

      if ((isBefore(a.date, date) || isEqual(a.date, date)) && (isBefore(date, b.date) || isEqual(date, b.date))) {
        const totalDays = differenceInCalendarDays(b.date, a.date);
        const daysElapsed = differenceInCalendarDays(date, a.date);

        const expected = a.value + (b.value - a.value) * (daysElapsed / totalDays);
        return { date, expected: parseFloat(expected.toFixed(decimalNumbers)) };
      }
    }

    return { date, expected: null };
  });
}

export function getExpectedFlatMetricValuesAtDates(
  outcome: TabilityTypes.Outcome,
  plan: TabilityTypes.Plan,
  referenceDates: Date[],
) {
  if (!plan.start_at || !plan.finish_at) {
    return [];
  }

  if (outcome.to === null) {
    return [];
  }

  // Check if target has a decimal number
  const targetHasDecimal = !Number.isInteger(outcome.to);

  const decimalNumbers = targetHasDecimal ? 2 : 0;

  const targetMilestones = JSON.parse(outcome.target_milestones) || [];
  const segments = [
    { date: parseISO(plan.start_at), value: outcome.to },
    ...targetMilestones.map((m: TabilityTypes.TargetMilestone) => ({
      date: parseISO(m.date),
      value: m.value,
    })),
    { date: parseISO(plan.finish_at), value: outcome.to },
  ].sort((a, b) => a.date - b.date);

  return referenceDates.map((date) => {
    // Skip before start or after end
    if (isBefore(date, segments[0].date) || isAfter(date, segments[segments.length - 1].date)) {
      return { date, expected: null };
    }

    // Find the segment the date falls into
    for (let i = 0; i < segments.length - 1; i++) {
      const a = segments[i];
      const b = segments[i + 1];

      if ((isBefore(a.date, date) || isEqual(a.date, date)) && (isBefore(date, b.date) || isEqual(date, b.date))) {
        const totalDays = differenceInCalendarDays(b.date, a.date);
        const daysElapsed = differenceInCalendarDays(date, a.date);
        const expected = a.value + (b.value - a.value) * (daysElapsed / totalDays);
        return { date, expected: parseFloat(expected.toFixed(decimalNumbers)) };
      }
    }

    return { date, expected: null };
  });
}

export function getExpectedKpiMetricValuesAtDates(plan: TabilityTypes.Plan, referenceDates: Date[]) {
  if (!plan.start_at || !plan.finish_at) {
    return [];
  }

  return referenceDates.map((date) => {
    return { date, expected: '-' };
  });
}

export function getExpectedNoMetricValuesAtDates(plan: TabilityTypes.Plan, referenceDates: Date[]) {
  if (!plan.start_at || !plan.finish_at) {
    return [];
  }

  return referenceDates.map((date) => {
    return { date, expected: 'on track' };
  });
}

export function cleanFormat(input: string | null) {
  if (!input) {
    return null;
  }
  const allowedSymbols = ['$', '€', '£', '¥', '₹', '₩', '₫', '₽', '฿', '%'];
  const placeholder = '_number_';
  let result = '';
  let i = 0;

  while (i < input.length) {
    if (input.slice(i, i + placeholder.length) === placeholder) {
      result += placeholder;
      i += placeholder.length;
      continue;
    }

    const char = input[i];
    if (/\d/.test(char) || char === '.' || allowedSymbols.includes(char)) {
      result += char;
    }

    i++;
  }

  return result;
}
